<template>
    <DialogLayout :class-name="'join-date-dialog'" @close="handleClose">
        <template #title>{{ dialogTitle }}</template>
        <template #default>
            <el-form ref="formRef" :model="formData" :rules="rules" :label-position="'left'" :label-width="'100px'"
                class="join-date-form">
                <el-form-item :label="t('candidate.joinDate1')" prop="joinDate" required>
                    <el-date-picker v-model="formData.joinDate" type="date" :placeholder="t('common.pleaseSelect')" format="YYYY-MM-DD"
                        value-format="YYYY-MM-DD" style="width: 100%" :disabled="props.disabledDate" />
                </el-form-item>

                <el-form-item :label="t('candidate.remarkInfo')" prop="remark">
                    <el-input v-model="formData.remark" type="textarea" :placeholder="t('common.pleaseEnter')" :rows="4" maxlength="200"
                        show-word-limit />
                </el-form-item>
            </el-form>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">
                {{ t('common.cancel') }}
            </el-button>
            <el-button type="primary" :loading="submitting" @click="handleConfirm">
                {{ confirmButtonText }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import DialogLayout from './dialog-layout.vue'
import type { JoinDateData, JoinDateDialogProps } from './join-date-dialog-types'
import { confirmJoinDateApi, updateJoinDateApi } from '@/api/candidate'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const props = defineProps<JoinDateDialogProps>()

const emit = defineEmits<{
    (e: 'confirm', data: JoinDateData): void
    (e: 'cancel'): void
    (e: 'close'): void
}>()

const formRef = ref<FormInstance>()
const submitting = ref(false)

// 按钮文本
const confirmButtonText = computed(() => {
    return props.data ? t('common.save') : t('common.confirm1')
})

// 对话框标题：如果有回显数据，则是修改模式
const dialogTitle = computed(() => {
    return props.data ? t('candidate.updateJoinDate') : t('candidate.confirmJoinDate')
})

// 表单数据
const formData = reactive<JoinDateData>({
    joinDate: '',
    remark: ''
})

// 表单验证规则
const rules: FormRules = {
    joinDate: [
        { required: true, message: t('candidate.pleaseSelectJoinDate'), trigger: 'change' }
    ]
}

// 初始化表单数据
onMounted(() => {
    if (props.data) {
        formData.joinDate = props.data.joinDate || ''
        formData.remark = props.data.remark || ''
    }
})

// 确认
const handleConfirm = async () => {
    if (!formRef.value) return

    try {
        // 先验证表单，如果验证失败会抛出错误，不会继续执行
        await formRef.value.validate()
    } catch (error: unknown) {
        // 表单验证失败，不显示额外提示（Element Plus 会自动显示），直接返回，不提交接口
        if (error && typeof error === 'object' && 'fields' in error) {
            return
        }
        // 其他验证错误也直接返回
        return
    }

    // 验证通过后，才执行 API 调用
    try {
        submitting.value = true

        // 根据是否有回显数据判断是确认还是修改
        const isUpdate = !!props.data

        if (isUpdate) {
            // 修改入职日期
            await updateJoinDateApi({
                interviewId: props.interviewId,
                stepId: props.stepId,
                joinDate: formData.joinDate!,
                remark: formData.remark || ''
            })
        } else {
            // 确认入职日期
            await confirmJoinDateApi({
                interviewId: props.interviewId,
                stepId: props.stepId,
                joinDate: formData.joinDate!,
                remark: formData.remark || ''
            })
        }

        // API 调用成功
        const resultData: JoinDateData = {
            joinDate: formData.joinDate,
            remark: formData.remark
        }
        
        ElMessage.success(t('common.saveSuccess'))
        submitting.value = false
        emit('confirm', resultData)
    } catch (error: unknown) {
        submitting.value = false
        
        // API 调用失败
        const errorMessage = error && typeof error === 'object' && 'message' in error 
            ? String(error.message) 
            : t('common.saveFailed')
        console.error('提交失败:', error)
        ElMessage.error(errorMessage)
    }
}

// 取消
const handleCancel = () => {
    emit('cancel')
}

// 关闭
const handleClose = () => {
    emit('close')
}
</script>

<style scoped lang="scss">
.join-date-form {

    :deep(.el-form-item__label) {
        font-size: 14px;
        font-weight: 500;
        color: #212429;
        line-height: 35px;
        text-align: right;
        padding-right: 12px;
        margin-bottom: 0px;

        &::before {
            display: none;
        }
    }

    :deep(.el-date-editor) {
        width: 100%;

        .el-input__wrapper {
            font-size: 14px;
            color: #495057;
        }
    }

    :deep(.el-textarea) {
        .el-textarea__inner {
            font-size: 14px;
            color: #495057;
            line-height: 22px;
            resize: none;

            &::placeholder {
                color: #C9D3D9;
            }
        }
    }

    :deep(.el-input__count) {
        font-size: 12px;
        color: #86909C;
        background: transparent;
    }
}

:deep(.join-date-dialog) {
    width: 512px;

   
}
</style>

<style lang="scss">
.join-date-dialog {
    width: 512px !important;
    .dialog-footer {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
    
            .el-button {
                min-width: 80px;
            }
        }
}
</style>

